<script lang="ts" setup>
/**
 * The menu of a circuit, as deep as its configuration nests: an Aquatouch keeps
 * the pages of a measurement in a group inside the control group, three levels
 * down. Indenting each level leaves a phone too little width by the third, so
 * a group opens in place of the list instead: every level gets the full width,
 * and a line on top names the way back and where the reader stands.
 */
import { computed, ref, watch } from 'vue'

import { ScreenItemType, type ISidebarItem } from '@/interfaces/gui'
import { translate } from '@/utils'

const props = defineProps<{
  items: ReadonlyArray<ISidebarItem>
}>()

const emit = defineEmits<{
  navigate: [route: string]
}>()

/** The groups opened from the top, the last one on show. */
const trail = ref<ReadonlyArray<ISidebarItem>>([])

// A new configuration brings a new menu; the opened groups belong to the old one.
watch(
  () => props.items,
  () => (trail.value = []),
)

const current = computed(() => trail.value[trail.value.length - 1])
const shown = computed(() => current.value?.items ?? props.items)
const way = computed(() =>
  trail.value
    .slice(0, -1)
    .map((group) => translate(group.label || ''))
    .join(' › '),
)

function open(group: ISidebarItem): void {
  trail.value = [...trail.value, group]
}

function back(): void {
  trail.value = trail.value.slice(0, -1)
}

function isSeparator(item: ISidebarItem): boolean {
  return item.type === ScreenItemType.Separator || item.type === ScreenItemType.Seperator
}

function glyph(icon: string): string {
  return String.fromCharCode(parseInt(icon, 16))
}
</script>

<template>
  <nav class="flex flex-col" data-component-name="SidebarMenu">
    <div v-if="current" class="mb-1 border-b border-slate-200 pb-2">
      <button
        class="menu-trail menu-back flex min-h-11 w-full items-center gap-4 rounded-md p-3 text-left transition-colors hover:bg-slate-100"
        type="button"
        @click="back"
      >
        <span class="sr-only">{{ translate('#back') }}</span>
        <span class="flex h-8 w-8 shrink-0 items-center justify-center text-slate-600">
          <svg
            class="h-6 w-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2.5"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </span>
        <span class="flex min-w-0 flex-1 flex-col justify-center">
          <span v-if="way" class="menu-way truncate text-xs leading-4 text-slate-500">{{
            way
          }}</span>
          <span class="menu-title truncate text-base leading-6 font-semibold text-slate-800">{{
            translate(current.label || '')
          }}</span>
        </span>
      </button>
    </div>

    <ul class="m-0 list-none p-0" :data-depth="trail.length">
      <template v-for="(item, index) in shown" :key="`${trail.length}-${index}`">
        <li v-if="item.type === ScreenItemType.Screenjump && item.screen" class="mb-1">
          <button
            class="menu-jump flex min-h-11 w-full items-center gap-4 rounded-md p-3 text-left transition-colors duration-200 hover:bg-slate-100"
            type="button"
            @click="emit('navigate', `/${item.screen}`)"
          >
            <span
              v-if="item.icon"
              class="font-icomoon icon flex h-8 w-8 shrink-0 items-center justify-center text-2xl text-slate-500"
              aria-hidden="true"
              >{{ glyph(item.icon) }}</span
            >
            <span class="menu-label flex-1 text-base text-slate-800">{{
              translate(item.label || '')
            }}</span>
          </button>
        </li>

        <li v-else-if="isSeparator(item)" class="menu-separator my-3 h-px bg-slate-200" />

        <li v-else-if="item.type === ScreenItemType.Extend" class="mb-1">
          <button
            class="menu-group flex min-h-11 w-full items-center gap-4 rounded-md p-3 text-left transition-colors duration-200 hover:bg-slate-100"
            type="button"
            @click="open(item)"
          >
            <span
              v-if="item.icon"
              class="font-icomoon icon flex h-8 w-8 shrink-0 items-center justify-center text-2xl text-slate-500"
              aria-hidden="true"
              >{{ glyph(item.icon) }}</span
            >
            <span class="menu-label flex-1 text-base text-slate-800">{{
              translate(item.label || '')
            }}</span>
            <svg
              class="h-3 w-3 shrink-0 text-slate-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="4"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </li>
      </template>
    </ul>
  </nav>
</template>
